import { Reducer } from 'redux';

import {
  CLEAR_SITE_ADMIN_RESOURCES,
  FETCH_SITE_ADMIN_RESOURCES_FAILURE,
  FETCH_SITE_ADMIN_RESOURCES_REQUEST,
  FETCH_SITE_ADMIN_RESOURCES_SUCCESS
} from '../actions/SiteAdminResourceActions';

/**
 * Redux state for site admin resources.
 * 
 * Stores which admin resource types (User, Group, Task, Job) the current
 * user can access, based on GetActorVisibleResourceTypesRequest API response.
 * 
 * Only populated when isAdminProfilesEnabledOnServer is ON.
 */
export interface SiteAdminResourcesState {
  /**
   * Set of resource types the user can access (e.g., 'User', 'Group', 'Task', 'Job')
   * undefined = not yet fetched
   * empty Set = fetched but no permissions
   */
  readonly allowedResources?: Set<string>;

  /**
   * Whether we're currently fetching from the API
   */
  readonly isFetching: boolean;
}

const initialState: SiteAdminResourcesState = {
  allowedResources: undefined,
  isFetching: false
};

/**
 * Reducer for site admin resources state.
 * 
 * Handles:
 * - Fetch request: Sets isFetching to true
 * - Fetch success: Stores allowed resources, clears fetching
 * - Fetch failure: Clears fetching (keeps existing resources)
 * - Clear: Resets to initial state (used on site switch)
 */
export const siteAdminResourcesReducer: Reducer<SiteAdminResourcesState> = (
  state = initialState,
  action
): SiteAdminResourcesState => {
  switch (action.type) {
    case FETCH_SITE_ADMIN_RESOURCES_REQUEST:
      return {
        ...state,
        isFetching: true
      };

    case FETCH_SITE_ADMIN_RESOURCES_SUCCESS:
      return {
        allowedResources: action.allowedResources,
        isFetching: false
      };

    case FETCH_SITE_ADMIN_RESOURCES_FAILURE:
      return {
        ...state,
        isFetching: false
      };

    case CLEAR_SITE_ADMIN_RESOURCES:
      return initialState;

    default:
      return state;
  }
};
